import React from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Col,
  Pane,
  PaneFooter,
  Paneset,
  Row,
  TextField,
  Checkbox,
  Accordion,
  AccordionSet,
} from '@folio/stripes-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { Field } from 'react-final-form';
import stripesFinalForm from '@folio/stripes/final-form';

function GeneralConfigForm({ handleSubmit, pristine, submitting, canManage = false, form }) {
  const intl = useIntl();

  const renderPaneFooter = () => (
    <PaneFooter
      renderEnd={
        <Button
          buttonStyle="primary"
          disabled={pristine || submitting || !canManage}
          id="general-config-settings-save-btn"
          marginBottom0
          type="submit"
        >
          <FormattedMessage id="stripes-core.button.save" />
        </Button>
      }
      renderStart={
        <Button
          buttonStyle="default"
          disabled={pristine || submitting || !canManage}
          id="general-config-settings-cancel-btn"
          marginBottom0
          onClick={() => form.reset()}
        >
          <FormattedMessage id="stripes-components.cancel" />
        </Button>
      }
    />
  );

  return (
    <form
      id="form-general-config-settings"
      onSubmit={handleSubmit}
      style={{ width: 'inherit' }}
    >
      <Paneset>
        <Pane
          fluidContentWidth
          footer={renderPaneFooter()}
          id="general-config-settings-pane"
          paneTitle={
            <FormattedMessage id="ui-reports.settings.generalConfig.paneTitle" />
          }
        >
          <AccordionSet>
            {/* Performance */}
            <Accordion
              id="general-config-performance"
              label={<FormattedMessage id="ui-reports.settings.generalConfig.performance.label" />}
            >
              <Row>
                <Col xs={4}>
                  <Field
                    name="maxQueryTimeout"
                    component={TextField}
                    type="number"
                    label={<FormattedMessage id="ui-reports.settings.generalConfig.maxQueryTimeout.label" />}
                    placeholder={intl.formatMessage({ id: 'ui-reports.settings.generalConfig.maxQueryTimeout.placeholder' })}
                  />
                </Col>
                <Col xs={4}>
                  <Field
                    name="reportCacheTtl"
                    component={TextField}
                    type="number"
                    label={<FormattedMessage id="ui-reports.settings.generalConfig.reportCacheTtl.label" />}
                    placeholder={intl.formatMessage({ id: 'ui-reports.settings.generalConfig.reportCacheTtl.placeholder' })}
                  />
                </Col>
                <Col xs={4}>
                  <Field
                    name="maxExportRows"
                    component={TextField}
                    type="number"
                    label={<FormattedMessage id="ui-reports.settings.generalConfig.maxExportRows.label" />}
                    placeholder={intl.formatMessage({ id: 'ui-reports.settings.generalConfig.maxExportRows.placeholder' })}
                  />
                </Col>
              </Row>
              <Row>
                <Col xs={4}>
                  <Field
                    name="enableQueryValidation"
                    component={Checkbox}
                    type="checkbox"
                    label={<FormattedMessage id="ui-reports.settings.generalConfig.enableQueryValidation.label" />}
                  />
                </Col>
              </Row>
            </Accordion>

            {/* Scheduling */}
            <Accordion
              id="general-config-scheduling"
              label={<FormattedMessage id="ui-reports.settings.generalConfig.scheduling.label" />}
            >
              <Row>
                <Col xs={4}>
                  <Field
                    name="enableScheduledReports"
                    component={Checkbox}
                    type="checkbox"
                    label={<FormattedMessage id="ui-reports.settings.generalConfig.enableScheduledReports.label" />}
                  />
                </Col>
                <Col xs={4}>
                  <Field
                    name="emailNotifications"
                    component={Checkbox}
                    type="checkbox"
                    label={<FormattedMessage id="ui-reports.settings.generalConfig.emailNotifications.label" />}
                  />
                </Col>
              </Row>
            </Accordion>

            {/* Dashboards */}
            <Accordion
              id="general-config-dashboards"
              label={<FormattedMessage id="ui-reports.settings.generalConfig.dashboards.label" />}
            >
              <Row>
                <Col xs={4}>
                  <Field
                    name="dashboardRefreshInterval"
                    component={TextField}
                    type="number"
                    label={<FormattedMessage id="ui-reports.settings.generalConfig.dashboardRefreshInterval.label" />}
                    placeholder={intl.formatMessage({ id: 'ui-reports.settings.generalConfig.dashboardRefreshInterval.placeholder' })}
                  />
                </Col>
              </Row>
            </Accordion>

            {/* Email / SMTP */}
            <Accordion
              id="general-config-smtp"
              label={<FormattedMessage id="ui-reports.settings.generalConfig.smtp.label" />}
            >
              <div style={{ marginBottom: '1rem', padding: '0.75rem', background: 'var(--color-background-warning, #fff3cd)', borderRadius: '4px', fontSize: '0.9rem' }}>
                <FormattedMessage id="ui-reports.settings.generalConfig.smtp.note" />
              </div>
              <Row>
                <Col xs={6}>
                  <Field
                    name="smtpHost"
                    component={TextField}
                    label={<FormattedMessage id="ui-reports.settings.generalConfig.smtp.host" />}
                    placeholder="smtp.gmail.com"
                    fullWidth
                  />
                </Col>
                <Col xs={2}>
                  <Field
                    name="smtpPort"
                    component={TextField}
                    type="number"
                    label={<FormattedMessage id="ui-reports.settings.generalConfig.smtp.port" />}
                    placeholder="587"
                  />
                </Col>
                <Col xs={4}>
                  <Field
                    name="fromEmail"
                    component={TextField}
                    label={<FormattedMessage id="ui-reports.settings.generalConfig.smtp.fromEmail" />}
                    placeholder="noreply@library.org"
                    fullWidth
                  />
                </Col>
              </Row>
              <Row>
                <Col xs={6}>
                  <Field
                    name="smtpUsername"
                    component={TextField}
                    label={<FormattedMessage id="ui-reports.settings.generalConfig.smtp.username" />}
                    fullWidth
                  />
                </Col>
                <Col xs={6}>
                  <Field
                    name="smtpPassword"
                    component={TextField}
                    type="password"
                    label={<FormattedMessage id="ui-reports.settings.generalConfig.smtp.password" />}
                    fullWidth
                  />
                </Col>
              </Row>
            </Accordion>
          </AccordionSet>
        </Pane>
      </Paneset>
    </form>
  );
}

GeneralConfigForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  pristine: PropTypes.bool,
  submitting: PropTypes.bool,
  form: PropTypes.object.isRequired,
};

export default stripesFinalForm({
  navigationCheck: true,
  subscription: { values: true },
  validateOnBlur: true,
})(GeneralConfigForm);
